import { CloseOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';

import { Button, Icon, Input, Text } from 'components';

import { Alert, Select } from 'antd';
import { CopyButton } from 'components/CopyButton';
import { SelectOption } from 'utils/OptionUtils';
import * as Styles from './FormGroupItem.styles';

export type FormGroupItemProps = {
  label: string;
  alternativeLabel: string;
  name: string;
  type?: string;
  value: string | null | number | undefined;
  alertDescription?: any;
  isValid?: (value: string | number) => boolean;
  onSave?: (text: any) => void;
  hasCopyIcon?: boolean;
  restrictEdit?: boolean;
  dropdown?: {
    values: SelectOption[];
    defaultValue?: string;
    placeholder: string;
    options: SelectOption[];
    allowClear?: boolean;
  };
};

const FormGroupItem: React.FC<FormGroupItemProps> = (props) => {
  const [isEditing, setEditing] = useState(false);
  const [value, setValue] = useState<string | number>(props.value ?? '');
  const [originalValue, setOriginalValue] = useState<string | number>(
    props.value ?? '',
  );
  const debouncedValue = useDebounce(value.toString(), 500);

  // Keep local state in sync when props.value changes (e.g., Firestore refresh)
  useEffect(() => {
    setValue(props.value ?? '');
    setOriginalValue(props.value ?? '');
  }, [props.value]);

  const displayValue = useMemo(() => {
    // ✅ Show dropdown label in view mode, while storing option.value underneath
    if (
      (props.type === 'dropdown' || props.type === 'country') &&
      props.dropdown?.options
    ) {
      const found = props.dropdown.options.find(
        (opt) => opt.value === props.value,
      );
      return found?.label ?? props.value ?? '';
    }
    return props.value ?? '';
  }, [props.type, props.value, props.dropdown?.options]);

  const handleToggleEditing = () => {
    if (!isEditing) {
      setOriginalValue(value);
    }
    setEditing((prev) => !prev);
  };

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setValue(event.target.value);
  };

  /**
   * Handle campaign (keeps your old behaviour)
   */
  const onHandleCampaignValues = () => {
    if (value === 'no-campaign') {
      props.onSave?.({ noCampaign: 'no-campaign' });
    } else {
      props.onSave?.({ qpCampaign: value });
    }
  };

  /**
   * Save the value
   */
  const handleSave = () => {
    if (props.name === 'campaign') {
      onHandleCampaignValues();
      setEditing(false);
      return;
    }

    if (value === 'true') {
      props.onSave?.(true);
    } else if (value === 'false') {
      props.onSave?.(false);
    } else {
      props.onSave?.(value);
    }

    setEditing(false);
  };

  const resetValue = () => setValue(originalValue);

  return (
    <Styles.Container>
      {!isEditing && (
        <>
          <Styles.LabelContainer>
            <Text size="ui3">
              <Styles.StyledDiv>
                {props.label}
                {props.hasCopyIcon && displayValue && (
                  <CopyButton text={displayValue.toString()} margin="0px 5px" />
                )}
              </Styles.StyledDiv>
            </Text>

            {props.isValid && !props.isValid?.(debouncedValue) && (
              <Alert
                message={`Warning: ${props.value} not valid ${props.name}`}
                type="warning"
                showIcon
                closable
                style={{ margin: '1rem 0' }}
              />
            )}

            <Styles.ValueText size="ui2">
              {displayValue as any}
            </Styles.ValueText>
          </Styles.LabelContainer>

          {!props.restrictEdit && (
            <Styles.ChangeButton type="text" onClick={handleToggleEditing}>
              Change
            </Styles.ChangeButton>
          )}
        </>
      )}

      {isEditing && (
        <Styles.Column>
          <Styles.Row>
            <Text size="ui2" bold>
              {props.alternativeLabel}
            </Text>

            <Button
              ghost
              onClick={() => {
                handleToggleEditing();
                resetValue();
              }}
            >
              <Icon icon={<CloseOutlined />} />
            </Button>
          </Styles.Row>

          <Styles.InputContainer onSubmit={handleSave}>
            <Text size="ui3">{props.label}</Text>

            <Styles.InputRow>
              {getChangeInput(props, handleChange, value, setValue)}
              <Styles.SaveButton
                onClick={handleSave}
                disabled={
                  props.isValid != null && !props.isValid?.(debouncedValue)
                }
              >
                Save
              </Styles.SaveButton>
            </Styles.InputRow>

            {props.isValid && !props.isValid?.(debouncedValue) && (
              <Alert
                message="Warning Invalid Field"
                description={props.alertDescription}
                type="warning"
                showIcon
                closable
                style={{ margin: '1rem 0' }}
              />
            )}
          </Styles.InputContainer>
        </Styles.Column>
      )}
    </Styles.Container>
  );
};

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [delay, value]);

  return debouncedValue;
}

const getChangeInput = (
  props: FormGroupItemProps,
  handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >,
  value: string | number,
  setValue: React.Dispatch<React.SetStateAction<string | number>>,
) => {
  const dropdownHandleChange = (v: string | number) => setValue(v);

  if (props.type === 'textarea') {
    return (
      <Input.TextArea name={props.name} value={value} onChange={handleChange} />
    );
  }

  if (props.type === 'dropdown') {
    if (!props?.dropdown?.options) {
      return null;
    }

    const selected = value ? value.toString() : props.dropdown.defaultValue;

    return (
      <Select
        style={{ width: '100%', maxWidth: '300px' }}
        showSearch
        placeholder={props.dropdown.placeholder}
        optionFilterProp="children"
        onChange={dropdownHandleChange}
        value={selected || undefined}
        allowClear={props.dropdown.allowClear}
        onClear={() => setValue('')}
      >
        {props.dropdown.options.map((opt) => (
          <Select.Option key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    );
  }

  // Country select
  if (props.type === 'country') {
    if (!props?.dropdown?.options) {
      return null;
    }

    return (
      <Select
        style={{ width: '100%', maxWidth: '300px' }}
        showSearch
        placeholder={props.dropdown.placeholder ?? 'Select country'}
        optionFilterProp="children"
        onChange={dropdownHandleChange}
        value={value ? value.toString() : undefined}
        allowClear={props.dropdown.allowClear}
        onClear={() => setValue('')}
      >
        {props.dropdown.options.map((opt) => (
          <Select.Option key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    );
  }

  return (
    <Input
      name={props.name}
      value={value}
      onChange={handleChange}
      type={props.type ? props.type : 'text'}
    />
  );
};

export { FormGroupItem };
